import axios from 'axios';
import { getTokenAuth } from '../utils/authUtils';
import {
  handleAxiosUnauthorizedError,
  installFetchUnauthorizedInterceptor
} from '../utils/authSessionManager';
import errorTranslationService from './errorTranslationService';
import i18n from '../i18n';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── GET response cache + in-flight deduplication ─────────────────────────────
// Purpose: prevent N identical GET requests fired in the same navigation burst
// from all hitting the server. Responses are reused for 8 seconds (covers the
// typical < 1 s window between concurrent React renders on the same page), then
// discarded so subsequent visits always fetch fresh data.
const _GET_CACHE_TTL = 8000; // ms
const _responseCache = new Map();  // cacheKey → { value, ts }
const _inflightRequests = new Map(); // cacheKey → Promise

function _buildCacheKey(url, params) {
  if (!params || Object.keys(params).length === 0) return url;
  const sorted = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .sort(([a], [b]) => a.localeCompare(b));
  return url + '?' + new URLSearchParams(sorted).toString();
}

const _originalGet = api.get.bind(api);
api.get = function cachedGet(url, config = {}) {
  // Allow callers to opt-out of caching (e.g. after a mutation)
  if (config._skipCache) return _originalGet(url, config);

  const cacheKey = _buildCacheKey(url, config.params);

  // 1. Serve from short-term cache if still fresh
  const cached = _responseCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < _GET_CACHE_TTL) {
    return Promise.resolve(cached.value);
  }

  // 2. Deduplicate concurrent calls for the same resource
  const inflight = _inflightRequests.get(cacheKey);
  if (inflight) return inflight;

  // 3. Issue the real request and populate both maps
  const promise = _originalGet(url, config)
    .then((response) => {
      _responseCache.set(cacheKey, { value: response, ts: Date.now() });
      _inflightRequests.delete(cacheKey);
      return response;
    })
    .catch((err) => {
      _inflightRequests.delete(cacheKey);
      throw err;
    });

  _inflightRequests.set(cacheKey, promise);
  return promise;
};

/**
 * Manually invalidate cached GET responses.
 * Call after mutations that affect the data returned by a URL prefix.
 * Pass no argument to wipe the entire cache.
 *
 * @example api.invalidateCache('/reservations')
 */
api.invalidateCache = (urlPattern) => {
  if (!urlPattern) {
    _responseCache.clear();
    return;
  }
  for (const key of _responseCache.keys()) {
    if (key.includes(urlPattern)) _responseCache.delete(key);
  }
};

installFetchUnauthorizedInterceptor();

// Intercepteur pour ajouter le token à chaque requête
api.interceptors.request.use(
  (config) => {
    console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`);
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0].toLowerCase();
    config.headers['Accept-Language'] = currentLanguage;
    const token = getTokenAuth();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API Request] Token added to request');
    } else {
      console.log('[API Request] No token available');
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.config.method.toUpperCase()} ${response.config.url} - Status: ${response.status}`);
    
    // Si la réponse contient des erreurs (ignorer les messages de succès)
    const responseData = response.data;

    const isMutableObject = responseData && typeof responseData === 'object' && !Array.isArray(responseData);

    if (isMutableObject && (responseData.error || (responseData.message && !responseData.success) || (responseData.msg && !responseData.success))) {
      console.log('[API Response] Response contains error or unsuccessful message', {
        error: responseData.error,
        message: responseData.message,
        msg: responseData.msg,
        success: responseData.success
      });
      
      // Conserver le message original
      const originalError = responseData.error || responseData.message || responseData.msg;
      console.log('[API Response] Original error message:', originalError);
      
      // Traduire l'erreur
      if (responseData.error) {
        const translatedError = errorTranslationService.translateErrorMessage(responseData.error);
        console.log('[API Response] Translated error:', responseData.error, '->', translatedError);
        responseData.error = translatedError;
      }
      if (responseData.message && !responseData.success) {
        const translatedMessage = errorTranslationService.translateErrorMessage(responseData.message);
        console.log('[API Response] Translated message:', responseData.message, '->', translatedMessage);
        responseData.message = translatedMessage;
      }
      if (responseData.msg && !responseData.success) {
        const translatedMsg = errorTranslationService.translateErrorMessage(responseData.msg);
        console.log('[API Response] Translated msg:', responseData.msg, '->', translatedMsg);
        responseData.msg = translatedMsg;
      }
      
      // Ajouter le message original pour référence
      responseData.originalError = originalError;
      console.log('[API Response] Final response data with translations:', responseData);
    } else if (!isMutableObject && responseData && (typeof responseData === 'string')) {
      console.log('[API Response] Non-object error payload detected, skipping mutation.');
    } else {
      console.log('[API Response] Response does not contain errors');
    }
    return response;
  },
  async (error) => {
    // Soft retry for rate-limited GETs (idempotent) to avoid transient 429 surfacing.
    // 3 retries with exponential backoff (1.5 s, 3 s, 6 s), respecting Retry-After.
    if (error?.response?.status === 429 && error?.config) {
      const config = error.config;
      const method = (config.method || 'get').toLowerCase();
      const retryCount = Number(config.__retryCount || 0);

      if (method === 'get' && retryCount < 3) {
        config.__retryCount = retryCount + 1;

        const retryAfterHeader = error?.response?.headers?.['retry-after'];
        let retryAfterMs = 0;
        if (retryAfterHeader) {
          const asNumber = Number(retryAfterHeader);
          if (!Number.isNaN(asNumber) && asNumber > 0) {
            retryAfterMs = asNumber * 1000;
          } else {
            const asDate = Date.parse(retryAfterHeader);
            if (!Number.isNaN(asDate)) {
              retryAfterMs = Math.max(0, asDate - Date.now());
            }
          }
        }

        // Exponential backoff: 1 500 ms → 3 000 ms → 6 000 ms
        const baseDelayMs = 1500 * Math.pow(2, retryCount);
        const jitterMs = Math.floor(Math.random() * 400);
        const delayMs = Math.min(12000, Math.max(baseDelayMs, retryAfterMs) + jitterMs);

        console.warn(`[API] 429 — retry ${config.__retryCount}/3 in ${delayMs}ms for ${config.url}`);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return api(config);
      }
    }

    // Handle 401 early — expired session, no need to translate or log further
    const unauthorizedPromise = handleAxiosUnauthorizedError(error);
    if (unauthorizedPromise) {
      console.log('[API] 401 — Session expirée, redirection en cours…');
      return unauthorizedPromise;
    }

    console.error('[API Error]', error);
    console.log('[API Error] Request that caused error:', error.config?.method?.toUpperCase(), error.config?.url);
    console.log('[API Error] Status:', error.response?.status);
    
    // Traduire le message d'erreur
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      const isMutableObject = typeof errorData === 'object' && errorData !== null && !Array.isArray(errorData);

      console.log('[API Error] Error response data:', errorData);
      
      // Conserver le message original
      const originalError = errorData.error || errorData.message || errorData.msg;
      console.log('[API Error] Original error message:', originalError);
      
      // Traduire l'erreur
      if (isMutableObject && errorData.error) {
        const translatedError = errorTranslationService.translateErrorMessage(errorData.error);
        console.log('[API Error] Translated error:', errorData.error, '->', translatedError);
        errorData.error = translatedError;
      }
      if (isMutableObject && errorData.message) {
        const translatedMessage = errorTranslationService.translateErrorMessage(errorData.message);
        console.log('[API Error] Translated message:', errorData.message, '->', translatedMessage);
        errorData.message = translatedMessage;
      }
      if (isMutableObject && errorData.msg) {
        const translatedMsg = errorTranslationService.translateErrorMessage(errorData.msg);
        console.log('[API Error] Translated msg:', errorData.msg, '->', translatedMsg);
        errorData.msg = translatedMsg;
      }
      
      // Ajouter le message original pour référence
      if (isMutableObject) {
        errorData.originalError = originalError;
        console.log('[API Error] Final error data with translations:', errorData);
      } else {
        error.response.originalError = originalError;
        console.log('[API Error] Non-object error payload, stored originalError on response object only.');
      }
    } else {
      console.log('[API Error] No response data available');
    }
    
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour extraire le message d'erreur traduit
api.getTranslatedErrorMessage = (error) => {
  console.log('[API Util] Getting translated error message for:', error);
  const translatedMessage = errorTranslationService.translateErrorMessage(error);
  console.log('[API Util] Translated message:', translatedMessage);
  return translatedMessage;
};

export default api;